import { useState, useEffect } from 'react';
import apiService, { getStorageUrl } from '../../../../services/apiService';
import { useTenant } from '../../../../contexts/TenantContext';

const ThrowballGallery = () => {
  const { siteSettings } = useTenant();
  const [galleryData, setGalleryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(null);
  const [expandedTournament, setExpandedTournament] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [years, setYears] = useState([]);

  useEffect(() => {
    fetchGalleryData();
  }, []);

  const fetchGalleryData = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/public/gallery-by-events');
      const data = response.data;

      if (data.success && data.data) {
        // Sort by date descending (latest first)
        const sortedData = [...data.data].sort((a, b) => {
          const dateA = new Date(a.event_date || 0);
          const dateB = new Date(b.event_date || 0);
          return dateB - dateA;
        });

        setGalleryData(sortedData);
        // Extract unique years and sort descending
        const uniqueYears = [...new Set(sortedData.map(item => item.year))].sort((a, b) => b - a);
        setYears(uniqueYears);
        // Set first year as default selected
        if (uniqueYears.length > 0) {
          setSelectedYear(uniqueYears[0]);
          // Auto-expand the first (latest) tournament
          const tournamentsInLatestYear = sortedData.filter(item => item.year === uniqueYears[0]);
          if (tournamentsInLatestYear.length > 0) {
            setExpandedTournament(tournamentsInLatestYear[0].id);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching gallery data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter tournaments by selected year
  const getTournamentsByYear = (year) => {
    return galleryData.filter(item => item.year === year);
  };

  const toggleTournament = (tournamentId) => {
    setExpandedTournament(expandedTournament === tournamentId ? null : tournamentId);
  };

  const openLightbox = (image) => {
    setSelectedImage(image);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  return (
    <section className="tb-page-section tb-gallery-page-section">
      <div className="tb-container">
        {/* Top Banner */}
        <div className="tb-page-banner">
          <img src={siteSettings?.page_banners?.gallery || '/theme/assets/images/banner-4.jpg'} alt="Gallery Banner" />
        </div>

        {/* Page Content */}
        <div className="tb-page-content">
          {/* Page Title */}
          <div className="tb-heading-top">
            <h1 className="tb-page-title">PHOTO GALLERY</h1>
            <span className="tb-mini-line"></span>
          </div>

          {loading ? (
            <div className="tb-gallery-loading">
              <i className="fas fa-spinner fa-spin"></i>
              <p>Loading gallery...</p>
            </div>
          ) : years.length === 0 ? (
            <div className="tb-gallery-empty">
              <i className="fas fa-images"></i>
              <p>No gallery images available yet.</p>
            </div>
          ) : (
            <div className="tb-gallery-content">
              {/* Year Tabs - Like Reference */}
              <div className="tb-year-tabs">
                {years.map((year) => (
                  <button
                    key={year}
                    className={`tb-year-tab ${selectedYear === year ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedYear(year);
                      setExpandedTournament(null);
                    }}
                  >
                    {year}
                  </button>
                ))}
              </div>

              {/* Tournament Accordion List */}
              <div className="tb-tournament-accordion">
                {getTournamentsByYear(selectedYear).map((tournament, index) => (
                  <div key={tournament.id} className="tb-accordion-item">
                    <div
                      className={`tb-accordion-header ${expandedTournament === tournament.id ? 'expanded' : ''}`}
                      onClick={() => toggleTournament(tournament.id)}
                    >
                      <span className="tb-accordion-title">
                        Leg-{index + 1}, {tournament.event_venue} — {tournament.event_name}
                      </span>
                      <i className={`fas fa-chevron-right tb-accordion-icon ${expandedTournament === tournament.id ? 'rotated' : ''}`}></i>
                    </div>

                    {/* Tournament Images - Expandable Content */}
                    {expandedTournament === tournament.id && (
                      <div className="tb-accordion-content">
                        {tournament.images && tournament.images.length > 0 ? (
                          <div className="tb-images-grid">
                            {tournament.images.map((image, imgIndex) => (
                              <div
                                key={image.id || imgIndex}
                                className="tb-image-item"
                                onClick={() => openLightbox(image.url)}
                              >
                                <img
                                  src={getStorageUrl(image.url) || ''}
                                  alt={image.caption || `${tournament.event_name} - Image ${imgIndex + 1}`}
                                />
                                <div className="tb-image-overlay">
                                  <i className="fas fa-search-plus"></i>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="tb-no-images">
                            <p>No images available for this tournament.</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}

                {getTournamentsByYear(selectedYear).length === 0 && (
                  <div className="tb-no-tournaments">
                    <p>No tournaments found for {selectedYear}.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {selectedImage && (
        <div className="tb-lightbox" onClick={closeLightbox}>
          <div className="tb-lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="tb-lightbox-close" onClick={closeLightbox}>
              <i className="fas fa-times"></i>
            </button>
            <img src={getStorageUrl(selectedImage) || ''} alt="Gallery" />
          </div>
        </div>
      )}

      <style>{`
        .tb-gallery-loading,
        .tb-gallery-empty {
          text-align: center;
          padding: 4rem 2rem;
        }

        .tb-gallery-loading i,
        .tb-gallery-empty i {
          font-size: 3rem;
          color: #c00f15;
          margin-bottom: 1rem;
          display: block;
        }

        .tb-gallery-loading p,
        .tb-gallery-empty p {
          color: #6b7280;
          font-size: 1.1rem;
        }

        .tb-gallery-content {
          padding: 1rem 0;
        }

        /* Year Tabs - Like Reference Image */
        .tb-year-tabs {
          display: flex;
          flex-wrap: wrap;
          gap: 0;
          margin-bottom: 2rem;
        }

        .tb-year-tab {
          padding: 0.75rem 1.5rem;
          background: #f3f4f6;
          border: none;
          font-size: 1.1rem;
          font-weight: 600;
          color: #374151;
          cursor: pointer;
          transition: all 0.2s;
          border-radius: 4px;
          margin-right: 0.5rem;
          margin-bottom: 0.5rem;
        }

        .tb-year-tab:hover {
          background: #e5e7eb;
        }

        .tb-year-tab.active {
          background: #c00f15;
          color: white;
        }

        /* Tournament Accordion - Like Reference Image */
        .tb-tournament-accordion {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .tb-accordion-item {
          background: white;
          border-radius: 4px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.08);
          border: 1px solid #e5e7eb;
        }

        .tb-accordion-header {
          background: #f9fafb;
          padding: 1rem 1.25rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          cursor: pointer;
          transition: all 0.2s;
          border-left: 4px solid transparent;
        }

        .tb-accordion-header:hover {
          background: #f3f4f6;
          border-left-color: #c00f15;
        }

        .tb-accordion-header.expanded {
          background: #f3f4f6;
          border-left-color: #c00f15;
        }

        .tb-accordion-title {
          color: #1f2937;
          font-size: 1rem;
          font-weight: 500;
        }

        .tb-accordion-icon {
          color: #6b7280;
          font-size: 0.9rem;
          transition: transform 0.3s;
        }

        .tb-accordion-icon.rotated {
          transform: rotate(90deg);
        }

        /* Accordion Content */
        .tb-accordion-content {
          padding: 1.5rem;
          background: white;
          border-top: 1px solid #e5e7eb;
        }

        .tb-images-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
          gap: 1rem;
        }

        .tb-image-item {
          position: relative;
          aspect-ratio: 4/3;
          border-radius: 6px;
          overflow: hidden;
          cursor: pointer;
          box-shadow: 0 2px 6px rgba(0,0,0,0.1);
        }

        .tb-image-item img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s;
        }

        .tb-image-item:hover img {
          transform: scale(1.05);
        }

        .tb-image-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.4);
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.3s;
        }

        .tb-image-item:hover .tb-image-overlay {
          opacity: 1;
        }

        .tb-image-overlay i {
          color: white;
          font-size: 1.5rem;
        }

        .tb-no-images,
        .tb-no-tournaments {
          text-align: center;
          padding: 2rem;
          color: #6b7280;
        }

        /* Lightbox */
        .tb-lightbox {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.9);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 2rem;
        }

        .tb-lightbox-content {
          position: relative;
          max-width: 90vw;
          max-height: 90vh;
        }

        .tb-lightbox-content img {
          max-width: 100%;
          max-height: 85vh;
          object-fit: contain;
          border-radius: 8px;
        }

        .tb-lightbox-close {
          position: absolute;
          top: -40px;
          right: 0;
          background: none;
          border: none;
          color: white;
          font-size: 2rem;
          cursor: pointer;
          padding: 0.5rem;
        }

        .tb-lightbox-close:hover {
          color: #c00f15;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .tb-year-tab {
            padding: 0.5rem 1rem;
            font-size: 0.95rem;
          }

          .tb-accordion-header {
            padding: 0.875rem 1rem;
          }

          .tb-accordion-title {
            font-size: 0.9rem;
          }

          .tb-accordion-content {
            padding: 1rem;
          }

          .tb-images-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 0.75rem;
          }
        }
      `}</style>
    </section>
  );
};

export default ThrowballGallery;
